import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { interval, take, timer, zip } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { ShippingCalculatorService } from '../../core/services/shipping-calculator.service';
import { formatPriceCents } from '../../core/utils/currency';
import type {
  SaddleBoxType,
  ShippingQuoteResult,
} from '../../core/models/shipping-calculator.model';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { PostcodeAutocompleteComponent } from '../../shared/components/postcode-autocomplete/postcode-autocomplete.component';
import { RevealDirective } from '../../shared/directives/reveal.directive';

const DEFAULT_VALUE_AUD = 500;

// A real AusPost quote often comes back in a couple hundred ms, which reads
// as "did that even call anything?" — holding the calculating state open to
// at least this long makes the live lookup legible rather than instant/
// suspicious. Real latency on top of this is never trimmed, only topped up.
const MIN_CALCULATING_MS = 2000;

const LOADING_MESSAGES = [
  'Checking your box dimensions…',
  'Getting a live Australia Post rate…',
  'Almost there…',
];

/**
 * Standalone, public "how much would it cost to post this saddle" tool —
 * separate from checkout's own delivery-fee calc (which needs a real
 * listing/buyer address). Reuses the exact same AustraliaPostService/box
 * profiles via ShippingController.quote, so numbers here match what a real
 * DELIVERY sale would actually be charged for the same postcodes/value.
 */
@Component({
  selector: 'app-shipping-calculator',
  standalone: true,
  imports: [RouterLink, IconComponent, PostcodeAutocompleteComponent, RevealDirective],
  templateUrl: './shipping-calculator.component.html',
})
export class ShippingCalculatorComponent {
  private readonly shippingCalculatorService = inject(ShippingCalculatorService);
  private readonly destroyRef = inject(DestroyRef);
  readonly authService = inject(AuthService);

  @ViewChild('fromInput') fromInput?: PostcodeAutocompleteComponent;
  @ViewChild('toInput') toInput?: PostcodeAutocompleteComponent;

  readonly formatPriceCents = formatPriceCents;

  readonly saddleType = signal<SaddleBoxType>('OTHER');
  readonly valueAud = signal(DEFAULT_VALUE_AUD);

  readonly submitting = signal(false);
  readonly loadingMessage = signal(LOADING_MESSAGES[0]);
  readonly errorMessage = signal<string | null>(null);
  readonly result = signal<ShippingQuoteResult | null>(null);

  calculate(): void {
    const fromPostcode = this.fromInput?.resolvedPostcode();
    const toPostcode = this.toInput?.resolvedPostcode();

    if (!fromPostcode || !toPostcode) {
      this.errorMessage.set('Choose a valid "from" and "to" postcode.');
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);
    this.result.set(null);
    this.loadingMessage.set(LOADING_MESSAGES[0]);

    // Cycles through the remaining messages over the minimum calculating
    // window below; harmless if the real request finishes first since the
    // whole subscription gets torn down with it.
    interval(MIN_CALCULATING_MS / LOADING_MESSAGES.length)
      .pipe(take(LOADING_MESSAGES.length - 1), takeUntilDestroyed(this.destroyRef))
      .subscribe((i) => this.loadingMessage.set(LOADING_MESSAGES[i + 1]));

    zip(
      this.shippingCalculatorService.getQuote({
        fromPostcode,
        toPostcode,
        saddleType: this.saddleType(),
        valueCents: Math.round(Number(this.valueAud()) * 100),
      }),
      timer(MIN_CALCULATING_MS),
    ).subscribe({
      next: ([result]) => {
        this.submitting.set(false);
        this.result.set(result);
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        this.errorMessage.set(this.extractErrorMessage(err));
      },
    });
  }

  private extractErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as { message?: string | string[] } | undefined;
      if (body?.message) {
        return Array.isArray(body.message) ? body.message.join(' ') : body.message;
      }
      if (err.status === 429) {
        return "You've made a few calculations in a row — wait a minute and try again.";
      }
    }
    return 'Could not calculate a quote right now — please try again.';
  }
}
